import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot, X, Send, Trash2, Maximize2, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAiChatStore } from '../stores/useAiChatStore';
import { useAiChat } from '../hooks/useAiChat';
import { AiChatMessage } from './AiChatMessage';
import { useHomepage } from '../../../hooks/useHomepage';
import '../ai-agent.css';

export const AiChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation('shop');
  const { data: homepageData } = useHomepage();
  const [input, setInput] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { isOpen, setIsOpen, hasShownWelcome, clearChat } = useAiChatStore();
  const { messages, isLoading, sendMessage } = useAiChat();

  // If chatbot is explicitly disabled, do not render anything.
  if (homepageData && homepageData.metadata.aiChatbotEnabled === false) {
    return null;
  }

  const location = useLocation();
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const isProductPage = location.pathname.startsWith('/product/');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      // Auto-focus input when opened on desktop
      if (window.innerWidth > 640) {
        setTimeout(() => inputRef.current?.focus(), 300);
      }
    }
  }, [messages, isOpen, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendMessage(input, homepageData?.metadata.aiChatbotWebhook || undefined);
    setInput('');
  };

  const handleClear = () => {
    if (window.confirm(t('aiChat.confirmClear', 'Are you sure you want to clear the chat?'))) {
      clearChat();
    }
  };

  const isRtl = i18n.dir() === 'rtl';

  return (
    <>
      {/* Floating Action Button */}
      <div 
        className={`ai-chat-fab ${isRtl ? 'right-4 lg:right-6' : 'left-4 lg:left-6'} ${isProductPage ? 'bottom-[100px] lg:bottom-6' : 'bottom-4 lg:bottom-6'} ${!hasShownWelcome ? 'pulsing' : ''} ${isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'}`}
        onClick={() => setIsOpen(true)}
        title={t('aiChat.fabTooltip', 'Chat with El Raay Assistant')}
      >
        <Bot className="w-8 h-8 text-white" />
      </div>

      {/* Chat Panel */}
      <div className={`ai-chat-panel ${isOpen ? 'open' : ''} ${isFullScreen ? 'fixed inset-0 w-full h-full max-h-screen rounded-none' : ''}`}>
        
        {/* Header */}
        <div className="ai-chat-header">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Bot className="w-6 h-6 text-yellow-400" />
              </div>
              <span className="absolute bottom-0 rtl:left-0 rtl:right-auto right-0 w-3 h-3 bg-green-500 border-2 border-[#2A2A2A] rounded-full"></span>
            </div>
            <div>
              <h3 className="font-semibold text-white">{t('aiChat.assistantName', 'El Raay Assistant')}</h3>
              <p className="text-xs text-gray-300">{t('aiChat.onlineStatus', 'Online 24/7')}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={handleClear}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-300 hover:text-white"
              title={t('aiChat.clearChatTooltip', 'Clear chat')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-300 hover:text-white hidden sm:block"
            >
              {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button 
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-300 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="ai-chat-messages bg-gray-50/50">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4 space-y-4 opacity-70">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center mb-2">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-lg text-gray-800">{t('aiChat.welcomeHello', 'Hello there! 👋')}</h4>
              <p className="text-sm text-gray-600 max-w-[250px]">
                {t('aiChat.welcomeDesc', 'I am your AI shopping assistant. Ask me to find glasses, suggest styles, or check prices.')}
              </p>
            </div>
          )}
          
          {messages.map((msg) => (
            <AiChatMessage key={msg.id} message={msg} />
          ))}
          
          {isLoading && (
            <div className="flex w-full justify-start">
              <div className="flex max-w-[85%] gap-2 flex-row">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <div className="bg-gray-100 rounded-2xl rtl:rounded-tr-sm ltr:rounded-tl-sm px-4 py-4 flex items-center gap-1.5 h-[42px]">
                  <div className="w-2 h-2 rounded-full ai-typing-dot" />
                  <div className="w-2 h-2 rounded-full ai-typing-dot" />
                  <div className="w-2 h-2 rounded-full ai-typing-dot" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="ai-chat-input-area flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('aiChat.placeholder', 'Ask about any eyewear...')}
            className="flex-1 bg-gray-100 border-none rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #FFD700 0%, #B8860B 100%)', color: 'white' }}
          >
            <Send className={`w-5 h-5 ${isRtl ? 'mr-0.5 rotate-180' : 'ml-0.5'}`} />
          </button>
        </form>
      </div>
      
      {/* Overlay for mobile to close when clicking outside */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};
